// @flow
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { useAsync } from "hooks";
import {
    type SearchItemDTO,
    type SearchResultRowDTO,
    SearchItemType,
} from "data/types";
import SearchResultContainer from "containers/SearchResult";
import styles from "./ArchiveTargetSearchItem.module.css";
import SearchFormHeader from "components/SearchFormHeader";
import {
    createSearchItemAsync,
    fixSearchFilterName,
} from "components/CommandActions/searchResult_form";
import Status from "components/Status";
import Constants from "data/constants";
import { type uppyFile, uppyPendingFilesMetaSelector } from "data/uppy";
import { tokenize } from "data/utils";

const updateFilesPlaceholders = (
    searchItem: SearchItemDTO,
    pendingUploads: Array<uppyFile>
) => {
    if (pendingUploads == null || pendingUploads.length === 0) return;
    if (searchItem.filters && searchItem.filters.length > 0) {
        searchItem.filters
            // process only filters actually including tokens/placeholders wrapped in {}
            .filter(
                (filter) =>
                    filter.value &&
                    String(filter.value).length > 0 &&
                    filter.value.indexOf("{") !== -1
            )
            .forEach((filter) => {
                // replace any tokens
                const newValue = tokenize(filter.value, pendingUploads[0]);
                // only if anything replaced set value
                if (newValue !== filter.value) {
                    filter.value = newValue;
                    filter.name = fixSearchFilterName(filter.name); //only overwrite value (see SearchQueryConversion.cs)
                }
            });
    }
};

type Props = {
    /** searchItem to render */
    searchItem: SearchItemDTO,
    /** callback when target found
     * @param {string} targetItemUri target's itemUri to archive onto
     */
    onTargetItemUri: (targetItemUri: string) => void,
    /** callback when user clicks the Cancel button */
    onCancel: () => void,
};

const ArchiveTargetSearchItem = ({
    searchItem,
    onTargetItemUri,
    onCancel,
}: Props) => {
    const pendingUploads = useSelector(uppyPendingFilesMetaSelector);
    const newSearchItem = useAsync(async () => {
        if (searchItem.searchType === SearchItemType.Form) {
            // process {geolocation}/{barcode} tokens requiring async operations
            const newSearchItem = await createSearchItemAsync(searchItem);
            updateFilesPlaceholders(newSearchItem, pendingUploads);
            return newSearchItem;
        } else {
            // only searchItem.id provided
            return searchItem;
        }
    }, [searchItem]);

    const onSearchFormSetTarget = useCallback(
        (activeRowIndex: number, row?: SearchResultRowDTO) => {
            /* istanbul ignore else */
            if (
                activeRowIndex !== -1 &&
                row != null &&
                typeof onTargetItemUri === "function"
            ) {
                onTargetItemUri(
                    // take itemUri without EID
                    row.breadcrumb.parts[row.breadcrumb.parts.length - 1]
                        .itemUri
                );
            }
            return false;
        },
        [onTargetItemUri]
    );

    return (
        <div className={styles.wrapper}>
            <SearchFormHeader
                searchForm={newSearchItem.value}
                onCancel={onCancel}
            />
            {newSearchItem.loading && <Status status={Constants.LOADING} />}
            {newSearchItem.error && <Status status={Constants.ERROR} />}
            {!newSearchItem.loading &&
                !newSearchItem.error &&
                newSearchItem.value && (
                    <div className={styles.searchResult}>
                        <SearchResultContainer
                            isBreadcrumbReadonly={true}
                            showFilterBar={true}
                            searchItem={newSearchItem.value}
                            onSetActiveRowIndex={onSearchFormSetTarget}
                            // extraFilters={[
                            //     {
                            //         name: "ItemType",
                            //         value: "Folder",
                            //         op: SearchOperator.Equals,
                            //         valueType: SearchValueType.ItemType
                            //     }
                            // ]}
                            isMoreHidden={true}
                        />
                    </div>
                )}
        </div>
    );
};

export default ArchiveTargetSearchItem;
